import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AdminPanel from "./AdminPanel";
import "./App.css";

const API_URL = "https://func-xalq-chatbot.azurewebsites.net/api/chat";
const FEEDBACK_URL = "https://func-xalq-chatbot.azurewebsites.net/api/feedback";
const CALLBACK_URL = "https://func-xalq-chatbot.azurewebsites.net/api/callback-request";

const isAdminRoute =
  window.location.pathname === "/admin" ||
  window.location.pathname === "/admin/";

// Generate a stable session ID for conversation logging.
const sessionId = (() => {
  const key = "chat_session_id";
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = `session-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  sessionStorage.setItem(key, id);
  return id;
})();

// Generates a unique id for each message so feedback can be tied to it.
let messageCounter = 0;
const genId = () => `msg-${Date.now()}-${messageCounter++}`;

const QUICK_REPLIES = [
  "What loans do you offer?",
  "Tell me about XalqKart cards",
  "How do I open an account?",
  "Tell me about deposits",
  "Corporate banking options",
  "How to use XalqOnline?",
];

function TypingDots() {
  return (
    <div className="typing-dots">
      <span /><span /><span />
    </div>
  );
}

function CallbackModal({ onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);

  const canSubmit = name.trim() && phone.trim() && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setModalError(null);
    try {
      const res = await fetch(CALLBACK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Request failed");
      }
      onSuccess();
    } catch {
      setModalError("Something went wrong. Please call us directly at 138.");
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">Request a callback</div>
        <div className="modal-subtitle">A bank advisor will call you back shortly</div>
        <form onSubmit={handleSubmit}>
          <input
            className="modal-input"
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            required
          />
          <input
            className="modal-input"
            type="tel"
            placeholder="+994 xx xxx xxxx"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={30}
            required
          />
          {modalError && <div className="modal-error">{modalError}</div>}
          <button
            type="submit"
            className={`modal-submit-btn ${canSubmit ? "modal-submit-btn--active" : ""}`}
            disabled={!canSubmit}
          >
            {submitting ? "Sending…" : "Request callback"}
          </button>
        </form>
        <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function Message({ msg }) {
  const isUser = msg.role === "user";
  const [rating, setRating] = useState(null);

  const sendFeedback = async (value) => {
    // Prevent double-clicks / re-rating — state updates immediately.
    if (rating) return;
    setRating(value);

    try {
      await fetch(FEEDBACK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messageId: msg.id,
          rating: value,
          messageText: msg.content,
        }),
      });
    } catch (err) {
      // Feedback is non-critical — fail silently so the chat keeps working.
    }
  };

  return (
    <div className={`msg-row ${isUser ? "msg-row--user" : "msg-row--bot"}`}>
      {!isUser && <div className="avatar">X</div>}
      <div className="msg-content">
        <div className={`bubble ${isUser ? "bubble--user" : "bubble--bot"}`}>
          {isUser
            ? <p>{msg.content}</p>
            : <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
          }
        </div>
        {!isUser && (
          <div className="feedback">
            <button
              className={`feedback-btn ${rating === "up" ? "feedback-btn--active" : ""}`}
              onClick={() => sendFeedback("up")}
              disabled={!!rating}
              aria-label="Helpful"
              title="Helpful"
            >
              👍
            </button>
            <button
              className={`feedback-btn ${rating === "down" ? "feedback-btn--active" : ""}`}
              onClick={() => sendFeedback("down")}
              disabled={!!rating}
              aria-label="Not helpful"
              title="Not helpful"
            >
              👎
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatApp() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      id: genId(),
      content: "Hello! I'm the Xalq Bank Azerbaijan assistant. I can help you with information about our banking products — loans, cards, accounts, savings, digital services, and more.\n\nHow can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQuick, setShowQuick] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;

    setInput("");
    setShowQuick(false);
    setError(null);

    const newMessages = [...messages, { role: "user", id: genId(), content: userText }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const history = newMessages.slice(-10).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userText, history, sessionId }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Request failed");
      }

      const reply = data.reply || "I'm sorry, I could not process that request.";
      setMessages((prev) => [...prev, { role: "assistant", id: genId(), content: reply }]);
    } catch (err) {
      setError("Connection error. Please try again or call 138.");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          id: genId(),
          content: "I'm having trouble connecting right now. Please call us at 138 or visit xalqbank.az.",
        },
      ]);
    }

    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCallbackSuccess = () => {
    setShowModal(false);
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        id: genId(),
        content: "✓ Callback requested. An advisor will contact you shortly.",
      },
    ]);
  };

  return (
    <div className="app">
      {showModal && (
        <CallbackModal
          onClose={() => setShowModal(false)}
          onSuccess={handleCallbackSuccess}
        />
      )}
      <div className="chat-window">

        {/* Header */}
        <div className="header">
          <div className="header-logo">X</div>
          <div className="header-info">
            <div className="header-name">Xalq Bank Azerbaijan</div>
            <div className="header-status">
              <span className="status-dot" />
              Virtual Banking Assistant
            </div>
          </div>
          <div className="header-phone">138</div>
          <button
            className="handoff-btn"
            onClick={() => setShowModal(true)}
            aria-label="Talk to a human"
          >
            Talk to a human
          </button>
        </div>

        {/* Messages */}
        <div className="messages">
          {messages.map((msg, i) => (
            <Message key={i} msg={msg} />
          ))}

          {loading && (
            <div className="msg-row msg-row--bot">
              <div className="avatar">X</div>
              <div className="bubble bubble--bot">
                <TypingDots />
              </div>
            </div>
          )}

          {showQuick && !loading && (
            <div className="quick-replies">
              {QUICK_REPLIES.map((q, i) => (
                <button key={i} className="quick-btn" onClick={() => sendMessage(q)}>
                  {q}
                </button>
              ))}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Error banner */}
        {error && <div className="error-banner">{error}</div>}

        {/* Input */}
        <div className="input-area">
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask about loans, cards, accounts, savings…"
            className="input-box"
          />
          <button
            className={`send-btn ${input.trim() && !loading ? "send-btn--active" : ""}`}
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            aria-label="Send message"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>

        {/* Disclaimer */}
        <div className="disclaimer">
          General information only — not financial advice. For personalized guidance visit a branch or call 138.
        </div>

      </div>
    </div>
  );
}

export default function App() {
  if (isAdminRoute) return <AdminPanel />;
  return <ChatApp />;
}
